/**
 * Admin write schemas for WebsiteSection (Phase 5 - CMS write API).
 *
 * dataJson is intentionally unknown() at the boundary; the API service
 * runs `parseSectionData(sectionType, dataJson)` from website-sections.ts
 * to validate the shape per sectionType. This keeps schema dispatch in
 * one place (the public read path already uses the same parser to drop
 * corrupt sections).
 */

import { z } from 'zod';

import { UuidSchema } from './common.js';

// Mirror prisma enum WebsiteSectionType. Adding a new section type means
// updating: prisma enum, this schema, and website-sections.ts parser.
export const WebsiteSectionTypeSchema = z.enum([
  'HERO',
  'PACKAGE_GRID',
  'PACKAGE_CAROUSELS',
  'PLACES_GRID',
  'ACTIVITIES_GRID',
  'EVENTS_GRID',
  'QUICK_INFO',
  'VISION_MISSION',
  'CELEBRATION',
  'CALLBACK_CHAT',
  'LANDING_PACKAGES',
  'CLIENT_STORIES',
  'REVIEWS',
  'FAQ_LIST',
  'BEST_TIME',
  'MONTH_BY_MONTH',
  'TRAVEL_STYLES',
  'RECOMMENDED_PACKAGES',
  'RICH_TEXT',
  'GALLERY',
  'CALLBACK_FORM',
  'MICE_FORM',
  'STATS_STRIP',
  'FEATURE_LIST',
  'PROCESS_STEPS',
  'VALUE_PROPS',
  'IMAGE_STRIP',
  'CTA_BANNER',
  'SERVICE_ICONS_ROW',
  'STAR_ENDORSEMENTS',
  'FEATURED_CATEGORY',
  'EDITORS_PICKS',
  'EXPLORE_STORIES',
  'AWARDS_BLOCK',
  'PARTNERS_LOGOS',
  'MICE_HERO',
  'MICE_ACCREDITATIONS',
  'MICE_PARTNERS_MARQUEE',
  'MICE_SERVICES',
  'MICE_CASE_STUDIES',
  'MICE_DESTINATIONS',
  'CLIENT_STORY_CARDS',
  'MICE_CLIENT_STORIES',
  'PRESS_LOGOS',
  'DESTINATION_SHOWCASE',
  'LOCATIONS',
  'TEXT_BLOCK',
  'TEAM_PROFILES',
  'FULL_IMAGE',
  'FEATURE_CARDS',
  'IMAGE_MARQUEE',
  'OPEN_POSITIONS',
  'LINK_GROUPS',
  'REELS_STRIP',
  'AWARDS_STATS',
  'VIDEO_REVIEWS',
  'TRAVELLER_REVIEWS',
  'HANDPICKED_STAYS',
  'EXPLORE_THEMES',
  'STAY_COLLECTION',
  'STAY_DESTINATIONS',
  'FLIGHT_DEALS',
  'ACTIVITY_PICKS',
]);
export type WebsiteSectionType = z.infer<typeof WebsiteSectionTypeSchema>;

export const CreateWebsiteSectionRequestSchema = z.object({
  pageId: UuidSchema,
  sectionType: WebsiteSectionTypeSchema,
  // Optional: when omitted, the server appends the section at the end
  // (max existing sortOrder + 1) instead of defaulting to 0 (which would tie
  // with the first section and surface near the top).
  sortOrder: z.number().int().min(0).optional(),
  title: z.string().max(255).nullish(),
  subtitle: z.string().max(500).nullish(),
  dataJson: z.unknown(),
  isVisible: z.boolean().default(true),
});
export type CreateWebsiteSectionRequest = z.infer<typeof CreateWebsiteSectionRequestSchema>;

export const UpdateWebsiteSectionRequestSchema = CreateWebsiteSectionRequestSchema.omit({
  pageId: true,
}).partial();
export type UpdateWebsiteSectionRequest = z.infer<typeof UpdateWebsiteSectionRequestSchema>;

// Reorder body is the new section-id sequence in display order. The service
// validates every id belongs to the pageId in the URL.
export const ReorderWebsiteSectionsRequestSchema = z.object({
  orderedSectionIds: z.array(UuidSchema).min(1).max(200),
});
export type ReorderWebsiteSectionsRequest = z.infer<typeof ReorderWebsiteSectionsRequestSchema>;
